import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { factsPalette, looksLikeEmailHtml, paintFactsCards, promoteSimpleDivs } from "@/lib/emailPreview";
import {
  MAIL_RULE_OPERATORS,
  closeToken,
  openToken,
  parseIfToken,
  rulePhrase,
  ruleSentence,
  ruleValueMode,
  type MailRule,
  type MailRuleOp,
} from "@/lib/templateConditions";
import { MAIL_TOKEN_LABELS, mailTokenLabel } from "@/lib/templatePlaceholders";

const BUTTONS = [
  { key: "button_booking", label: "Zur Buchung" },
  { key: "button_shop", label: "Zum Shop" },
] as const;

const ACTION_KEYS = new Set<string>(BUTTONS.map((item) => item.key));

const IF_TOKEN_RE =
  /\{\{\s*#(if|unless)\s+([a-zA-Z0-9_]+)([^}]*)\}\}((?:(?!\{\{\s*#(?:if|unless)\b)[\s\S])*?)\{\{\s*\/\1\s*\}\}/gi;
const WORD_TOKEN_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\|\s*([^|{}]+?)\s*\|\s*([^|{}]+?)\s*\}\}/g;

export type EmailHtmlEditorHandle = {
  insertPlaceholder: (key: string) => void;
  focus: () => void;
};

export type MailConditionField = {
  key: string;
  label: string;
};

function escapeHtml(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

const FORMAT = {
  bold: { attr: "data-mail-bold", closest: "strong, b" },
  italic: { attr: "data-mail-italic", closest: "em, i" },
  underline: { attr: "data-mail-underline", closest: "u" },
} as const;

type FormatKind = keyof typeof FORMAT;

const TEXT_SIZES = [
  { label: "Klein", value: "13px" },
  { label: "Normal", value: "16px" },
  { label: "Groß", value: "20px" },
  { label: "Sehr groß", value: "28px" },
] as const;

const TEXT_COLORS = [
  { label: "Schwarz", value: "#18181b" },
  { label: "Grau", value: "#52525b" },
  { label: "Rot", value: "#b91c1c" },
  { label: "Orange", value: "#c2410c" },
  { label: "Grün", value: "#166534" },
  { label: "Blau", value: "#1d4ed8" },
  { label: "Türkis", value: "#0f766e" },
] as const;

function safeFontSize(value: string): string {
  const match = /^(\d{1,2}(?:\.\d+)?)px$/.exec(value.trim());
  if (!match) return "";
  const size = Number(match[1]);
  if (size < 10 || size > 48) return "";
  return `${size}px`;
}

function safeColor(value: string): string {
  const text = value.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(text)) return text.toLowerCase();
  const rgb = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+)?\s*\)$/.exec(text);
  if (!rgb) return "";
  const parts = [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  if (parts.some((part) => part > 255)) return "";
  return `#${parts.map((part) => part.toString(16).padStart(2, "0")).join("")}`;
}

const DIVIDER_HTML =
  '<hr data-mail-divider="1" style="border:0;border-top:1px solid #d4d4d8;margin:18px 0;" />';

function factsCardHtml(layoutId: string): string {
  const palette = factsPalette(layoutId);
  return (
    '<table data-mail-card="facts" role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" ' +
    `bgcolor="${palette.bg}" cellpadding="0" cellspacing="0" ` +
    `style="margin:16px 0;background:${palette.bg};background-color:${palette.bg};border:1px solid ${palette.border};border-radius:16px;border-collapse:separate;">` +
    "<tr>" +
    factsCell(palette, "Anreise", "<div>10–12 Uhr</div><div>15–18 Uhr</div>", false) +
    factsCell(palette, "Abreise", "<div>Bis 11 Uhr</div>", true) +
    factsCell(
      palette,
      "Bezahlung",
      `<div>Bar / EC</div><div data-mail-card-note="1" style="margin-top:4px;font-size:13px;line-height:1.3;font-weight:500;color:${palette.note};">Keine Kreditkarten</div>`,
      true,
    ) +
    "</tr></table>"
  );
}

function factsCell(
  palette: ReturnType<typeof factsPalette>,
  label: string,
  valueHtml: string,
  divider: boolean,
): string {
  const border = divider ? `border-left:1px solid ${palette.border};` : "";
  return (
    `<td width="33%" valign="top" style="padding:16px 14px;${border}color:${palette.value};font-family:${palette.font};">` +
    `<div data-mail-card-label="1" style="font-size:13px;line-height:1.3;font-weight:600;color:${palette.label};">${label}</div>` +
    `<div data-mail-card-value="1" style="margin-top:8px;font-size:18px;line-height:1.35;font-weight:700;color:${palette.value};">${valueHtml}</div>` +
    "</td>"
  );
}

function baseChipStyle(key: string): string {
  if (ACTION_KEYS.has(key)) {
    return "display:inline-block;margin:2px 6px 2px 0;padding:8px 14px;border-radius:8px;background:#166534;color:#ffffff;font-weight:700;font-size:14px;line-height:1.2;";
  }
  if (key === "salutation") {
    return "display:inline-block;margin:0 3px;padding:1px 10px;border-radius:999px;background:#0f766e;color:#ffffff;font-weight:650;font-size:0.92em;line-height:1.5;vertical-align:baseline;";
  }
  return "display:inline-block;margin:0 3px;padding:1px 8px;border-radius:999px;background:#eef2ff;color:#1e3a8a;font-weight:600;font-size:0.92em;line-height:1.5;border:1px solid #c7d2fe;vertical-align:baseline;";
}

function chipHasFormat(el: HTMLElement, kind: FormatKind): boolean {
  const spec = FORMAT[kind];
  return el.getAttribute(spec.attr) === "1" || Boolean(el.closest(spec.closest));
}

function wordChipStyle(): string {
  return "display:inline-block;margin:0 3px;padding:1px 8px;border-radius:999px;background:#fef3c7;color:#92400e;font-weight:600;font-size:0.92em;line-height:1.5;border:1px solid #fcd34d;vertical-align:baseline;";
}

function chipLook(el: HTMLElement): { fontSize: string; color: string } {
  let fontSize = safeFontSize(el.getAttribute("data-mail-size") || "");
  let color = safeColor(el.getAttribute("data-mail-color") || "");
  const root = el.closest("[role='textbox']");
  let node = el.parentElement;
  while (node && node !== root) {
    if (!fontSize) fontSize = safeFontSize(node.style.fontSize);
    if (!color) color = safeColor(node.style.color);
    node = node.parentElement;
  }
  return { fontSize, color };
}

function paintChip(el: HTMLElement) {
  const key = el.getAttribute("data-mail-token") || "";
  let style = el.hasAttribute("data-mail-word") ? wordChipStyle() : baseChipStyle(key);
  if (chipHasFormat(el, "bold")) style += "font-weight:800;";
  if (chipHasFormat(el, "italic")) style += "font-style:italic;";
  if (chipHasFormat(el, "underline")) style += "text-decoration:underline;";
  const look = chipLook(el);
  if (look.fontSize) style += `font-size:${look.fontSize};`;
  if (look.color) style += `color:${look.color};background:transparent;border-color:${look.color};`;
  el.setAttribute("style", style);
}

function paintChips(root: ParentNode) {
  root.querySelectorAll<HTMLElement>("[data-mail-token], [data-mail-word]").forEach(paintChip);
}

function paintLook(el: HTMLElement, patch: { color?: string; fontSize?: string }) {
  if (patch.color !== undefined) {
    const color = safeColor(patch.color);
    if (color) el.setAttribute("data-mail-color", color);
    else el.removeAttribute("data-mail-color");
  }
  if (patch.fontSize !== undefined) {
    const size = safeFontSize(patch.fontSize);
    if (size) el.setAttribute("data-mail-size", size);
    else el.removeAttribute("data-mail-size");
  }
}

function absorbChipOnlyStyles(root: HTMLElement) {
  root.querySelectorAll<HTMLElement>("[data-mail-token], [data-mail-word]").forEach((chip) => {
    let parent = chip.parentElement;
    while (parent && parent !== root && parent.childNodes.length === 1) {
      const tag = parent.tagName;
      if (tag === "STRONG" || tag === "B" || tag === "EM" || tag === "I" || tag === "U") {
        parent = parent.parentElement;
        continue;
      }
      if (tag !== "SPAN" && tag !== "FONT") break;
      const size = safeFontSize(parent.style.fontSize);
      const color = safeColor(parent.style.color || parent.getAttribute("color") || "");
      if (size) chip.setAttribute("data-mail-size", size);
      if (color) chip.setAttribute("data-mail-color", color);
      parent.style.fontSize = "";
      parent.style.color = "";
      parent.removeAttribute("color");
      parent.removeAttribute("size");
      const next = parent.parentElement;
      if (!parent.getAttribute("style")?.trim()) parent.removeAttribute("style");
      if (parent.attributes.length === 0) parent.replaceWith(...Array.from(parent.childNodes));
      parent = next;
    }
  });
}

function normalizeFontTags(root: HTMLElement, pendingSize: string) {
  Array.from(root.querySelectorAll("font")).forEach((font) => {
    const color = safeColor(font.getAttribute("color") || "");
    const size = font.getAttribute("size") === "7" ? safeFontSize(pendingSize) : "";
    const children = Array.from(font.childNodes);
    if (!color && !size) {
      font.replaceWith(...children);
      return;
    }
    const span = document.createElement("span");
    if (color) span.style.color = color;
    if (size) span.style.fontSize = size;
    span.append(...children);
    font.replaceWith(span);
  });
}

function rangeCovers(range: Range, node: Node): boolean {
  try {
    const probe = document.createRange();
    probe.selectNodeContents(node);
    return (
      range.compareBoundaryPoints(Range.START_TO_START, probe) <= 0
      && range.compareBoundaryPoints(Range.END_TO_END, probe) >= 0
    );
  } catch {
    return false;
  }
}

function unwrapBareSpan(span: HTMLElement) {
  if (span.tagName !== "SPAN") return;
  if (span.hasAttribute("data-mail-token") || span.hasAttribute("data-mail-word")) return;
  if (span.getAttribute("style")?.trim()) return;
  span.removeAttribute("style");
  if (span.attributes.length > 0) return;
  span.replaceWith(...Array.from(span.childNodes));
}

function textNodesInRange(field: HTMLElement, range: Range): Text[] {
  const nodes: Text[] = [];
  const walker = document.createTreeWalker(field, NodeFilter.SHOW_TEXT);
  let current = walker.nextNode();
  while (current) {
    const text = current as Text;
    current = walker.nextNode();
    if (!text.data) continue;
    try {
      if (range.intersectsNode(text)) nodes.push(text);
    } catch {
      // Der Knoten hängt nicht mehr im Dokument.
    }
  }
  return nodes;
}

function sliceText(node: Text, start: number, end: number): Text {
  let target = node;
  if (end < target.length) target.splitText(end);
  if (start > 0) target = target.splitText(start);
  return target;
}

function applyLookToText(field: HTMLElement, range: Range, patch: { color?: string; fontSize?: string }) {
  const covered = new Set<HTMLElement>();
  field.querySelectorAll<HTMLElement>("span").forEach((span) => {
    if (span.hasAttribute("data-mail-token") || span.hasAttribute("data-mail-word")) return;
    if (span.closest("[data-mail-token], [data-mail-word], [data-mail-if-chrome]")) return;
    if (!rangeCovers(range, span)) return;
    if (patch.color !== undefined) span.style.color = safeColor(patch.color);
    if (patch.fontSize !== undefined) span.style.fontSize = safeFontSize(patch.fontSize);
    unwrapBareSpan(span);
    covered.add(span);
  });
  for (const node of textNodesInRange(field, range)) {
    const parent = node.parentElement;
    if (!parent || parent.closest("[data-mail-token], [data-mail-word], [data-mail-if-chrome]")) continue;
    if (Array.from(covered).some((span) => span.contains(node))) continue;
    let start = 0;
    let end = node.length;
    if (node === range.startContainer) start = Math.min(range.startOffset, node.length);
    if (node === range.endContainer) end = Math.min(range.endOffset, node.length);
    if (start >= end) continue;
    const target = sliceText(node, start, end);
    const host = target.parentElement;
    if (
      host
      && host.tagName === "SPAN"
      && host.childNodes.length === 1
      && !host.hasAttribute("data-mail-token")
      && !host.hasAttribute("data-mail-word")
    ) {
      if (patch.color !== undefined) host.style.color = safeColor(patch.color);
      if (patch.fontSize !== undefined) host.style.fontSize = safeFontSize(patch.fontSize);
      unwrapBareSpan(host);
      continue;
    }
    const color = patch.color !== undefined ? safeColor(patch.color) : "";
    const size = patch.fontSize !== undefined ? safeFontSize(patch.fontSize) : "";
    if (!color && !size) continue;
    const span = document.createElement("span");
    if (color) span.style.color = color;
    if (size) span.style.fontSize = size;
    target.replaceWith(span);
    span.appendChild(target);
  }
}

function extractFromAncestor(ancestor: HTMLElement, node: HTMLElement) {
  let current: HTMLElement = node;
  while (current.parentElement && current.parentElement !== ancestor && ancestor.contains(current.parentElement)) {
    current = current.parentElement;
  }
  if (current.parentElement !== ancestor) return;
  const parent = ancestor.parentNode;
  if (!parent) return;
  const after = ancestor.cloneNode(false) as HTMLElement;
  let sibling = current.nextSibling;
  while (sibling) {
    const next = sibling.nextSibling;
    after.appendChild(sibling);
    sibling = next;
  }
  parent.insertBefore(current, ancestor.nextSibling);
  if (after.childNodes.length > 0) parent.insertBefore(after, current.nextSibling);
  if (ancestor.childNodes.length === 0) ancestor.remove();
}

function setChipFormat(field: HTMLElement, chip: HTMLElement, kind: FormatKind, on: boolean) {
  const spec = FORMAT[kind];
  if (on) {
    if (chip.closest(spec.closest)) chip.removeAttribute(spec.attr);
    else chip.setAttribute(spec.attr, "1");
  } else {
    chip.removeAttribute(spec.attr);
    const ancestor = chip.closest(spec.closest);
    if (ancestor instanceof HTMLElement && field.contains(ancestor)) extractFromAncestor(ancestor, chip);
  }
  paintChip(chip);
}

function chipsTouchingRange(field: HTMLElement, range: Range): HTMLElement[] {
  return Array.from(field.querySelectorAll<HTMLElement>("[data-mail-token], [data-mail-word]")).filter((chip) => {
    try {
      return range.intersectsNode(chip);
    } catch {
      return false;
    }
  });
}

function rangeHasEditableText(range: Range): boolean {
  if (range.collapsed) return true;
  const holder = document.createElement("div");
  holder.appendChild(range.cloneContents());
  holder.querySelectorAll("[data-mail-token], [data-mail-word], [data-mail-if-chrome]").forEach((el) => el.remove());
  const text = (holder.textContent || "").replace(/\u00a0/g, " ").trim();
  return text.length > 0 || Boolean(holder.querySelector("br, img, hr, table, ul, ol"));
}

function formattedToken(el: HTMLElement, html: string): Node {
  if (el.getAttribute("data-mail-underline") === "1") html = `<u>${html}</u>`;
  if (el.getAttribute("data-mail-italic") === "1") html = `<em>${html}</em>`;
  if (el.getAttribute("data-mail-bold") === "1") html = `<strong>${html}</strong>`;
  const size = safeFontSize(el.getAttribute("data-mail-size") || "");
  const color = safeColor(el.getAttribute("data-mail-color") || "");
  if (size || color) {
    const css = [size ? `font-size:${size}` : "", color ? `color:${color}` : ""].filter(Boolean).join(";");
    html = `<span style="${css}">${html}</span>`;
  }
  if (!html.startsWith("<")) return document.createTextNode(html);
  const holder = document.createElement("template");
  holder.innerHTML = html;
  return holder.content;
}

function tokenReplacement(el: HTMLElement): Node {
  const key = el.getAttribute("data-mail-token") || "";
  return formattedToken(el, `{{${key}}}`);
}

function wordReplacement(el: HTMLElement): Node {
  const key = el.getAttribute("data-mail-word") || "";
  const one = el.getAttribute("data-mail-one") || "";
  const many = el.getAttribute("data-mail-many") || "";
  return formattedToken(el, `{{${key}|${one}|${many}}}`);
}

function chipHtml(key: string): string {
  const label = escapeHtml(mailTokenLabel(key));
  const token = escapeHtml(key);
  return `<span data-mail-token="${token}" contenteditable="false" style="${baseChipStyle(key)}">${label}</span>`;
}

function wordChipHtml(key: string, one: string, many: string): string {
  const token = escapeHtml(key);
  const singular = escapeHtml(one);
  const plural = escapeHtml(many);
  return `<span data-mail-word="${token}" data-mail-one="${singular}" data-mail-many="${plural}" contenteditable="false" style="${wordChipStyle()}">${singular}/${plural}</span>`;
}

function replaceTokens(source: string): string {
  const withWords = source.replace(WORD_TOKEN_RE, (match, key: string, one: string, many: string) => {
    if (!MAIL_TOKEN_LABELS[key]) return match;
    return wordChipHtml(key, one.trim(), many.trim());
  });
  return withWords.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (match, key: string) => {
    if (!MAIL_TOKEN_LABELS[key]) return match;
    return chipHtml(key);
  });
}

function withChips(source: string): string {
  return source
    .split(/(<[^>]*>)/g)
    .map((part) => (part.startsWith("<") ? part : replaceTokens(part)))
    .join("");
}

function plainBlocks(piece: string): string {
  const trimmed = piece.replace(/^\n+/, "").replace(/\n+$/, "");
  if (!trimmed) return "";
  return trimmed
    .split(/\n[ \t]*\n/)
    .map((block) => {
      const inner = withChips(escapeHtml(block).replace(/\n/g, "<br>"));
      return `<p>${inner || "<br>"}</p>`;
    })
    .join("");
}

function cleanRuleText(value: string): string {
  return value.replace(/[{}|"']/g, "").replace(/\s+/g, " ").trim();
}

function ruleAttributes(rule: MailRule): string {
  const key = escapeHtml(rule.key);
  if (rule.op === "empty") return `data-mail-unless="${key}"`;
  const parts = [`data-mail-if="${key}"`];
  if (rule.op !== "filled") {
    parts.push(`data-mail-op="${escapeHtml(rule.op)}"`);
    parts.push(`data-mail-a="${escapeHtml(rule.a)}"`);
    if (rule.op === "between") parts.push(`data-mail-b="${escapeHtml(rule.b)}"`);
  }
  return parts.join(" ");
}

function conditionChromeHtml(rule: MailRule): string {
  const phrase = escapeHtml(rulePhrase(rule, mailTokenLabel(rule.key)));
  const buttonStyle = "border:0;background:transparent;color:#99f6e4;font:inherit;font-weight:650;cursor:pointer;padding:0;";
  return (
    `<div contenteditable="false" data-mail-if-chrome="1" style="display:flex;justify-content:space-between;align-items:center;gap:8px;margin:0 0 6px;font-size:12px;font-weight:650;line-height:1.3;color:#5eead4;">` +
    `<span data-mail-if-label="1">${phrase}</span>` +
    `<span style="display:flex;gap:10px;flex:none;">` +
    `<button type="button" data-mail-if-edit="1" style="${buttonStyle}">Bearbeiten</button>` +
    `<button type="button" data-mail-if-remove="1" style="${buttonStyle}">Bedingung lösen</button>` +
    `</span></div>`
  );
}

function conditionBlockHtml(rule: MailRule, innerHtml: string): string {
  return (
    `<div data-mail-block="1" ${ruleAttributes(rule)} style="margin:8px 0;padding:8px 10px 6px;border:1px dashed #2dd4bf;border-radius:10px;">` +
    conditionChromeHtml(rule) +
    `<div data-mail-if-body="1">${innerHtml || "<br>"}</div>` +
    `</div>`
  );
}

function ruleFromElement(el: HTMLElement): MailRule {
  const key = el.getAttribute("data-mail-if") || el.getAttribute("data-mail-unless") || "";
  const op = el.getAttribute("data-mail-op") || "";
  if (!op) {
    return { key, op: el.hasAttribute("data-mail-unless") ? "empty" : "filled", a: "", b: "" };
  }
  return {
    key,
    op: op as MailRuleOp,
    a: el.getAttribute("data-mail-a") || "",
    b: el.getAttribute("data-mail-b") || "",
  };
}

function writeRule(el: HTMLElement, rule: MailRule) {
  el.removeAttribute("data-mail-if");
  el.removeAttribute("data-mail-unless");
  el.removeAttribute("data-mail-op");
  el.removeAttribute("data-mail-a");
  el.removeAttribute("data-mail-b");
  if (rule.op === "empty") {
    el.setAttribute("data-mail-unless", rule.key);
  } else {
    el.setAttribute("data-mail-if", rule.key);
    if (rule.op !== "filled") {
      el.setAttribute("data-mail-op", rule.op);
      el.setAttribute("data-mail-a", rule.a);
      if (rule.op === "between") el.setAttribute("data-mail-b", rule.b);
    }
  }
  const label = el.querySelector("[data-mail-if-label]");
  if (label) label.textContent = rulePhrase(rule, mailTokenLabel(rule.key));
}

function tokensToBlocks(source: string): string {
  let current = source;
  for (let pass = 0; pass < 8; pass += 1) {
    IF_TOKEN_RE.lastIndex = 0;
    const updated = current.replace(IF_TOKEN_RE, (_match, kind: string, key: string, expression: string, inner: string) => {
      const rule = parseIfToken(kind, key, expression ?? "");
      const text = inner.replace(/^\n+/, "").replace(/\n+$/, "");
      return `\u0000COND${conditionBlockHtml(rule, withChips(escapeHtml(text).replace(/\n/g, "<br>")))}\u0000`;
    });
    if (updated === current) return current;
    current = updated;
  }
  return current;
}

function toEditorHtml(value: string): string {
  const marked = tokensToBlocks(value);
  const htmlSource = looksLikeEmailHtml(value);
  return marked
    .split("\u0000")
    .map((piece, index) => {
      if (index % 2 === 1) return piece.startsWith("COND") ? piece.slice(4) : piece;
      if (htmlSource) return withChips(piece);
      return plainBlocks(piece);
    })
    .join("");
}

function decorateConditionDivs(root: HTMLElement) {
  const blocks = Array.from(root.querySelectorAll<HTMLElement>("div[data-mail-if], div[data-mail-unless]"));
  const depth = (el: HTMLElement) => {
    let level = 0;
    let parent = el.parentElement;
    while (parent) {
      level += 1;
      parent = parent.parentElement;
    }
    return level;
  };
  blocks.sort((left, right) => depth(right) - depth(left));
  for (const el of blocks) {
    if (el.firstElementChild?.hasAttribute("data-mail-if-chrome")) continue;
    const rule = ruleFromElement(el);
    const inner = el.innerHTML;
    el.setAttribute("data-mail-block", "1");
    el.setAttribute("style", "margin:8px 0;padding:8px 10px 6px;border:1px dashed #2dd4bf;border-radius:10px;");
    el.innerHTML = `${conditionChromeHtml(rule)}<div data-mail-if-body="1">${inner || "<br>"}</div>`;
  }
}

function nodeToPlain(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? "";
  if (node instanceof HTMLElement && node.tagName === "BR") return "\n";
  let text = "";
  node.childNodes.forEach((child) => {
    text += nodeToPlain(child);
  });
  if (node instanceof HTMLElement && (node.tagName === "DIV" || node.tagName === "P" || node.tagName === "LI")) {
    if (!text.endsWith("\n")) text += "\n";
  }
  return text;
}

function editorToStored(root: HTMLElement): string {
  const clone = root.cloneNode(true) as HTMLElement;
  clone.querySelectorAll<HTMLElement>("[data-mail-word]").forEach((el) => {
    el.replaceWith(wordReplacement(el));
  });
  clone.querySelectorAll<HTMLElement>("[data-mail-token]").forEach((el) => {
    el.replaceWith(tokenReplacement(el));
  });
  const hasRich = Boolean(clone.querySelector("a, strong, b, em, i, u, span, font, ul, ol, h1, h2, h3, img, table, hr"));
  const blocks = Array.from(clone.querySelectorAll<HTMLElement>("[data-mail-block], [data-mail-if], [data-mail-unless]"));
  blocks.reverse().forEach((el) => {
    el.querySelectorAll("[data-mail-if-chrome]").forEach((chrome) => chrome.remove());
    const rule = ruleFromElement(el);
    const bodyEl = el.querySelector<HTMLElement>("[data-mail-if-body]");
    if (!hasRich) {
      const text = nodeToPlain(bodyEl ?? el).replace(/^\n+/, "").replace(/\n+$/, "");
      el.replaceWith(document.createTextNode(`\n${openToken(rule)}\n${text}\n${closeToken(rule)}\n`));
      return;
    }
    if (bodyEl) el.innerHTML = bodyEl.innerHTML;
    el.removeAttribute("style");
    el.removeAttribute("data-mail-block");
    el.removeAttribute("contenteditable");
    writeRule(el, rule);
  });
  promoteSimpleDivs(clone);
  const hasBlocks = Boolean(clone.querySelector("p, div, table, ul, ol, hr, h1, h2, h3, img"));
  if (hasRich || hasBlocks) {
    const text = (clone.textContent || "").replace(/\u00a0/g, " ").trim();
    if (!text && !clone.querySelector("img, table, hr, ul, ol")) return "";
    return clone.innerHTML;
  }
  return nodeToPlain(clone).replace(/^\n+/, "").replace(/\n+$/, "");
}

const WORD_PRESETS: Record<string, { one: string; many: string }[]> = {
  nights: [
    { one: "Nacht", many: "Nächte" },
    { one: "Nacht", many: "Nächten" },
  ],
  adults: [
    { one: "Erwachsener", many: "Erwachsene" },
    { one: "Erwachsenem", many: "Erwachsenen" },
  ],
  children: [
    { one: "Kind", many: "Kinder" },
    { one: "Kind", many: "Kindern" },
  ],
  dogs: [
    { one: "Hund", many: "Hunde" },
    { one: "Hund", many: "Hunden" },
  ],
  persons: [{ one: "Person", many: "Personen" }],
  days_until_arrival: [
    { one: "Tag", many: "Tage" },
    { one: "Tag", many: "Tagen" },
  ],
  days_until_departure: [
    { one: "Tag", many: "Tage" },
  ],
};

function suggestionsFor(key: string): { label: string; op: MailRuleOp; a: string; b: string }[] {
  const year = new Date().getFullYear();
  const specific: Record<string, { label: string; op: MailRuleOp; a: string; b: string }[]> = {
    nights: [
      { label: "unter 14 Nächten", op: "lt", a: "14", b: "" },
      { label: "mindestens 7", op: "gte", a: "7", b: "" },
      { label: "genau 1 Nacht", op: "eq", a: "1", b: "" },
      { label: "länger als 2 Wochen", op: "gt", a: "14", b: "" },
      { label: "1 bis 3 Nächte", op: "between", a: "1", b: "3" },
    ],
    days_until_arrival: [
      { label: "in den nächsten 7 Tagen", op: "lte", a: "7", b: "" },
      { label: "erst in über 30 Tagen", op: "gt", a: "30", b: "" },
    ],
    days_until_departure: [
      { label: "in den nächsten 3 Tagen", op: "lte", a: "3", b: "" },
    ],
    adults: [
      { label: "eine Person", op: "eq", a: "1", b: "" },
      { label: "mindestens 2", op: "gte", a: "2", b: "" },
      { label: "ohne Angabe", op: "empty", a: "", b: "" },
    ],
    children: [
      { label: "mit Kindern", op: "filled", a: "", b: "" },
      { label: "ohne Kinder", op: "empty", a: "", b: "" },
      { label: "mehr als 2", op: "gt", a: "2", b: "" },
    ],
    dogs: [
      { label: "mit Hund", op: "filled", a: "", b: "" },
      { label: "ohne Hund", op: "empty", a: "", b: "" },
    ],
    persons: [
      { label: "höchstens 4", op: "lte", a: "4", b: "" },
      { label: "mehr als 4", op: "gt", a: "4", b: "" },
    ],
    total_amount: [
      { label: "unter 100 €", op: "lt", a: "100", b: "" },
      { label: "mindestens 500 €", op: "gte", a: "500", b: "" },
    ],
    open_amount: [
      { label: "noch etwas offen", op: "gt", a: "0", b: "" },
      { label: "unter 50 €", op: "lt", a: "50", b: "" },
    ],
    stay_price: [
      { label: "unter 100 €", op: "lt", a: "100", b: "" },
      { label: "mindestens 500 €", op: "gte", a: "500", b: "" },
    ],
    vehicle_length_m: [
      { label: "länger als 8 m", op: "gt", a: "8", b: "" },
      { label: "höchstens 6 m", op: "lte", a: "6", b: "" },
    ],
    arrival_date: [
      { label: `vor dem 1. Juli ${year}`, op: "before", a: `01.07.${year}`, b: "" },
      { label: `im Juli oder August`, op: "between", a: `01.07.${year}`, b: `31.08.${year}` },
      { label: `nach dem 31. August ${year}`, op: "after", a: `31.08.${year}`, b: "" },
    ],
    departure_date: [
      { label: `vor dem 1. September ${year}`, op: "before", a: `01.09.${year}`, b: "" },
    ],
    booking_notes: [
      { label: "mit Notiz", op: "filled", a: "", b: "" },
      { label: "ohne Notiz", op: "empty", a: "", b: "" },
    ],
    category_name: [
      { label: "ist genau", op: "eq", a: "", b: "" },
      { label: "enthält", op: "contains", a: "", b: "" },
      { label: "ist eines von", op: "oneof", a: "", b: "" },
    ],
  };
  return specific[key] ?? [
    { label: "hat einen Wert", op: "filled", a: "", b: "" },
    { label: "ist leer", op: "empty", a: "", b: "" },
    { label: "kleiner als 14", op: "lt", a: "14", b: "" },
    { label: "mindestens 1", op: "gte", a: "1", b: "" },
    { label: "zwischen", op: "between", a: "", b: "" },
    { label: "enthält", op: "contains", a: "", b: "" },
  ];
}

function valueHint(op: MailRuleOp, which: "a" | "b"): string {
  if (op === "before" || op === "after" || op === "on") return "01.07.2026";
  if (op === "between") return which === "a" ? "7 oder 01.06.2026" : "14 oder 31.08.2026";
  if (op === "oneof") return "Komfort, Premium";
  if (op === "contains" || op === "not_contains" || op === "starts" || op === "ends" || op === "eq" || op === "ne") {
    return "14 oder Komfort";
  }
  return "14";
}

export const EmailHtmlEditor = forwardRef<
  EmailHtmlEditorHandle,
  {
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    onFocus?: () => void;
    minHeightClass?: string;
    conditionFields?: MailConditionField[];
    layoutId?: string;
  }
>(function EmailHtmlEditor(
  { value, onChange, disabled, onFocus, minHeightClass = "min-h-56", conditionFields = [], layoutId },
  ref,
) {
  const fieldRef = useRef<HTMLDivElement>(null);
  const emitted = useRef<string | null>(null);
  const savedRange = useRef<Range | null>(null);
  const editingBlock = useRef<HTMLElement | null>(null);
  const [ruleOpen, setRuleOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [ruleKey, setRuleKey] = useState(conditionFields[0]?.key ?? "");
  const [ruleOp, setRuleOp] = useState<MailRuleOp>("filled");
  const [ruleA, setRuleA] = useState("");
  const [ruleB, setRuleB] = useState("");
  const [wordOpen, setWordOpen] = useState(false);
  const [wordKey, setWordKey] = useState(conditionFields[0]?.key ?? "children");
  const [wordOne, setWordOne] = useState("Kind");
  const [wordMany, setWordMany] = useState("Kindern");
  const [sizePick, setSizePick] = useState("");
  const pendingSize = useRef("");

  useEffect(() => {
    if (conditionFields.some((field) => field.key === ruleKey)) return;
    setRuleKey(conditionFields[0]?.key ?? "");
  }, [conditionFields, ruleKey]);

  useEffect(() => {
    const el = fieldRef.current;
    if (!el || emitted.current === value) return;
    el.innerHTML = toEditorHtml(value);
    promoteSimpleDivs(el);
    decorateConditionDivs(el);
    absorbChipOnlyStyles(el);
    paintChips(el);
    document.execCommand("defaultParagraphSeparator", false, "p");
    paintFactsCards(el, layoutId || "classic");
    emitted.current = value;
  }, [layoutId, value]);

  useEffect(() => {
    const el = fieldRef.current;
    if (!el) return;
    paintFactsCards(el, layoutId || "classic");
  }, [layoutId, value]);

  function emit() {
    const el = fieldRef.current;
    if (!el) return;
    normalizeFontTags(el, pendingSize.current);
    const stored = editorToStored(el);
    emitted.current = stored;
    onChange(stored);
  }

  function restoreSelection() {
    const field = fieldRef.current;
    const range = savedRange.current;
    if (!field || !range) return;
    field.focus();
    const selection = window.getSelection();
    if (!selection) return;
    selection.removeAllRanges();
    try {
      selection.addRange(range);
    } catch {
      savedRange.current = null;
    }
  }

  function applyTextLook(patch: { color?: string; fontSize?: string }) {
    const field = fieldRef.current;
    if (!field || disabled) return;
    field.focus();
    const selection = window.getSelection();
    let range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    if (!range || !field.contains(range.startContainer)) {
      if (savedRange.current && field.contains(savedRange.current.startContainer)) {
        range = savedRange.current;
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }
    if (!range || !field.contains(range.startContainer)) return;
    const chips = range.collapsed ? [] : chipsTouchingRange(field, range);
    for (const chip of chips) {
      paintLook(chip, patch);
      paintChip(chip);
    }
    if (range.collapsed) {
      if (patch.color) {
        document.execCommand("styleWithCSS", false, "true");
        document.execCommand("foreColor", false, safeColor(patch.color));
      }
      if (patch.fontSize) {
        pendingSize.current = safeFontSize(patch.fontSize);
        document.execCommand("styleWithCSS", false, "false");
        document.execCommand("fontSize", false, "7");
      }
      return;
    }
    if (rangeHasEditableText(range)) applyLookToText(field, range, patch);
    emit();
  }

  function insertHtml(html: string) {
    fieldRef.current?.focus();
    document.execCommand("insertHTML", false, html);
    emit();
  }

  function insertBlock(html: string) {
    insertHtml(`${html}<p><br></p>`);
  }

  function applyInlineFormat(kind: FormatKind) {
    const field = fieldRef.current;
    if (!field || disabled) return;
    field.focus();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!field.contains(range.startContainer)) return;
    const chips = chipsTouchingRange(field, range);
    const commandOn = document.queryCommandState(kind);
    const hasText = rangeHasEditableText(range);
    const turningOn = chips.length > 0 ? chips.some((chip) => !chipHasFormat(chip, kind)) : !commandOn;
    if (hasText && commandOn !== turningOn) document.execCommand(kind);
    for (const chip of chips) setChipFormat(field, chip, kind, turningOn);
    emit();
  }

  function rememberSelection() {
    const field = fieldRef.current;
    const selection = window.getSelection();
    if (!field || !selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (field.contains(range.startContainer)) savedRange.current = range.cloneRange();
  }

  function draftRule(): MailRule {
    return { key: ruleKey, op: ruleOp, a: cleanRuleText(ruleA), b: cleanRuleText(ruleB) };
  }

  function insertConditionBlock(rule: MailRule) {
    const field = fieldRef.current;
    if (!field || !rule.key || disabled) return;
    field.focus();
    const selection = window.getSelection();
    let innerHtml = "<br>";
    let range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    let inside = Boolean(range && field.contains(range.startContainer));
    if (!inside && savedRange.current && field.contains(savedRange.current.startContainer)) {
      range = savedRange.current;
      inside = true;
    }
    if (range && inside && !range.collapsed) {
      const holder = document.createElement("div");
      holder.appendChild(range.cloneContents());
      innerHtml = holder.innerHTML || "<br>";
      range.deleteContents();
    }
    const holder = document.createElement("div");
    holder.innerHTML = conditionBlockHtml(rule, innerHtml);
    const block = holder.firstElementChild;
    if (!block) return;
    if (range && inside) {
      range.collapse(false);
      range.insertNode(block);
    } else {
      field.appendChild(block);
    }
    const body = block.querySelector("[data-mail-if-body]");
    if (body) {
      const caret = document.createRange();
      caret.selectNodeContents(body);
      caret.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(caret);
    }
    emit();
    setRuleOpen(false);
    setEditing(false);
    editingBlock.current = null;
  }

  function applyCondition() {
    const rule = draftRule();
    const block = editingBlock.current;
    if (editing && block?.isConnected) {
      writeRule(block, rule);
      emit();
      setRuleOpen(false);
      setEditing(false);
      editingBlock.current = null;
      return;
    }
    insertConditionBlock(rule);
  }

  function beginEdit(block: HTMLElement) {
    const rule = ruleFromElement(block);
    editingBlock.current = block;
    setEditing(true);
    setRuleKey(rule.key);
    setRuleOp(rule.op);
    setRuleA(rule.a);
    setRuleB(rule.b);
    setWordOpen(false);
    setRuleOpen(true);
  }

  function cancelEdit() {
    setEditing(false);
    editingBlock.current = null;
    setRuleOpen(false);
  }

  function releaseCondition(block: Element) {
    const body = block.querySelector("[data-mail-if-body]");
    const parent = block.parentNode;
    if (!body || !parent) return;
    while (body.firstChild) parent.insertBefore(body.firstChild, block);
    block.remove();
    if (editingBlock.current === block) {
      editingBlock.current = null;
      setEditing(false);
    }
    emit();
  }

  function insertWordChip() {
    const one = cleanRuleText(wordOne);
    const many = cleanRuleText(wordMany);
    if (!wordKey || !one || !many || disabled) return;
    insertHtml(wordChipHtml(wordKey, one, many));
    setWordOpen(false);
  }

  useImperativeHandle(ref, () => ({
    focus: () => fieldRef.current?.focus(),
    insertPlaceholder: (key: string) => {
      if (MAIL_TOKEN_LABELS[key]) {
        insertHtml(chipHtml(key));
        return;
      }
      fieldRef.current?.focus();
      document.execCommand("insertText", false, `{{${key}}}`);
      emit();
    },
  }));

  const ruleLabel = conditionFields.find((field) => field.key === ruleKey)?.label ?? mailTokenLabel(ruleKey);
  const rule = draftRule();
  const valueMode = ruleValueMode(ruleOp);
  const canApply = Boolean(rule.key) && (valueMode === "none" || Boolean(rule.a && (valueMode === "one" || rule.b)));
  const fieldOptions = conditionFields.some((field) => field.key === ruleKey) || !ruleKey
    ? conditionFields
    : [{ key: ruleKey, label: mailTokenLabel(ruleKey) }, ...conditionFields];
  const wordFields = conditionFields.length > 0 ? conditionFields : [
    { key: "nights", label: "Nächte" },
    { key: "adults", label: "Erwachsene" },
    { key: "children", label: "Kinder" },
    { key: "dogs", label: "Hunde" },
    { key: "persons", label: "Personen" },
  ];
  const operatorGroups = [...new Set(MAIL_RULE_OPERATORS.map((item) => item.group))];
  const suggestions = suggestionsFor(ruleKey);
  const wordPresets = WORD_PRESETS[wordKey] ?? [];
  const zeroHint = ruleOp === "filled" || ruleOp === "empty"
    ? " 0 gilt als leer."
    : " Bei Vergleichen zählt 0 als Zahl.";

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        <ToolbarButton disabled={disabled} title="Markierten Text oder Platzhalter fett setzen" onClick={() => applyInlineFormat("bold")}>Fett</ToolbarButton>
        <ToolbarButton disabled={disabled} title="Markierten Text oder Platzhalter kursiv setzen" onClick={() => applyInlineFormat("italic")}>Kursiv</ToolbarButton>
        <ToolbarButton disabled={disabled} title="Markierten Text oder Platzhalter unterstreichen" onClick={() => applyInlineFormat("underline")}>Unterstrichen</ToolbarButton>
        <select
          aria-label="Schriftgröße"
          title="Größe von markiertem Text oder Platzhalter"
          className="eh-input py-1 pl-2 pr-7 text-xs"
          style={{ width: "auto" }}
          value={sizePick}
          disabled={disabled}
          onMouseDown={() => rememberSelection()}
          onChange={(event) => {
            const next = event.target.value;
            setSizePick("");
            restoreSelection();
            applyTextLook({ fontSize: next === "reset" ? "" : next });
          }}
        >
          <option value="">Größe</option>
          {TEXT_SIZES.map((item) => (
            <option key={item.value} value={item.value}>{item.label}</option>
          ))}
          <option value="reset">Standard</option>
        </select>
        <span className="inline-flex items-center gap-1 px-0.5" role="group" aria-label="Textfarbe">
          {TEXT_COLORS.map((item) => (
            <button
              key={item.value}
              type="button"
              title={item.label}
              aria-label={item.label}
              disabled={disabled}
              className="h-5 w-5 rounded-full border border-black/15 disabled:opacity-50"
              style={{ backgroundColor: item.value }}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => applyTextLook({ color: item.value })}
            />
          ))}
          <input
            type="color"
            aria-label="Eigene Farbe"
            title="Eigene Farbe für markierten Text oder Platzhalter"
            disabled={disabled}
            className="h-7 w-8 cursor-pointer rounded border border-separator bg-card p-0.5 disabled:opacity-50"
            defaultValue="#1d4ed8"
            onMouseDown={() => rememberSelection()}
            onChange={(event) => {
              restoreSelection();
              applyTextLook({ color: event.target.value });
            }}
          />
          <ToolbarButton disabled={disabled} title="Farbe von markiertem Text oder Platzhalter zurücksetzen" onClick={() => applyTextLook({ color: "" })}>
            Farbe aus
          </ToolbarButton>
        </span>
        <ToolbarButton disabled={disabled} onClick={() => { fieldRef.current?.focus(); document.execCommand("insertUnorderedList"); emit(); }}>Liste</ToolbarButton>
        <ToolbarButton
          disabled={disabled}
          onClick={() => {
            const url = window.prompt("Adresse des Links", "https://");
            if (!url?.trim()) return;
            fieldRef.current?.focus();
            document.execCommand("createLink", false, url.trim());
            emit();
          }}
        >
          Link
        </ToolbarButton>
        <ToolbarButton disabled={disabled} title="Waagerechte Linie einfügen" onClick={() => insertBlock(DIVIDER_HTML)}>
          Trennlinie
        </ToolbarButton>
        <ToolbarButton
          disabled={disabled}
          title="Karte mit Anreise, Abreise und Bezahlung. Den Text darin können Sie ändern und Platzhalter hineinsetzen."
          onClick={() => insertBlock(factsCardHtml(layoutId || "classic"))}
        >
          Info-Karte
        </ToolbarButton>
        {BUTTONS.map((button) => (
          <ToolbarButton key={button.key} disabled={disabled} onClick={() => insertHtml(chipHtml(button.key))}>
            {button.label}
          </ToolbarButton>
        ))}
        {conditionFields.length > 0 ? (
          <ToolbarButton
            disabled={disabled}
            onClick={() => {
              rememberSelection();
              setWordOpen(false);
              setRuleOpen((open) => {
                if (open) {
                  setEditing(false);
                  editingBlock.current = null;
                }
                return !open;
              });
            }}
          >
            Absatz nur wenn…
          </ToolbarButton>
        ) : null}
        <ToolbarButton
          disabled={disabled}
          title="Wort, das bei 1 in die Einzahl wechselt. Zum Beispiel Kind statt Kindern."
          onClick={() => {
            rememberSelection();
            setRuleOpen(false);
            setWordOpen((open) => !open);
          }}
        >
          Zahlwort…
        </ToolbarButton>
      </div>
      {ruleOpen && conditionFields.length > 0 ? (
        <div className="space-y-2 rounded-xl border border-separator bg-fill-tertiary/40 p-3">
          <p className="text-sm text-label-secondary">
            {editing
              ? "Sie ändern die Bedingung. Der Absatz bleibt stehen. "
              : null}
            {ruleSentence(rule, ruleLabel)}
            {zeroHint}
            {" "}Markierten Text nimmt die Bedingung mit.
          </p>
          <div className="flex flex-wrap gap-1">
            {suggestions.map((item) => (
              <ToolbarButton
                key={item.label}
                disabled={disabled}
                onClick={() => {
                  setRuleOp(item.op);
                  setRuleA(item.a);
                  setRuleB(item.b);
                }}
              >
                {item.label}
              </ToolbarButton>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="eh-input min-w-40"
              value={ruleKey}
              onChange={(event) => setRuleKey(event.target.value)}
            >
              {fieldOptions.map((field) => (
                <option key={field.key} value={field.key}>
                  {field.label}
                </option>
              ))}
            </select>
            <select
              className="eh-input"
              value={ruleOp}
              onChange={(event) => setRuleOp(event.target.value as MailRuleOp)}
            >
              {operatorGroups.map((group) => (
                <optgroup key={group} label={group}>
                  {MAIL_RULE_OPERATORS.filter((item) => item.group === group).map((item) => (
                    <option key={item.op} value={item.op}>
                      {item.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {valueMode !== "none" ? (
              <input
                className="eh-input w-36"
                value={ruleA}
                placeholder={valueHint(ruleOp, "a")}
                onChange={(event) => setRuleA(event.target.value)}
              />
            ) : null}
            {valueMode === "two" ? (
              <>
                <span className="text-sm text-label-secondary">und</span>
                <input
                  className="eh-input w-36"
                  value={ruleB}
                  placeholder={valueHint(ruleOp, "b")}
                  onChange={(event) => setRuleB(event.target.value)}
                />
              </>
            ) : null}
            <ToolbarButton disabled={disabled || !canApply} onClick={applyCondition}>
              {editing ? "Übernehmen" : "Absatz einfügen"}
            </ToolbarButton>
            {editing ? (
              <ToolbarButton disabled={disabled} onClick={cancelEdit}>
                Abbrechen
              </ToolbarButton>
            ) : null}
          </div>
        </div>
      ) : null}
      {wordOpen ? (
        <div className="space-y-2 rounded-xl border border-separator bg-fill-tertiary/40 p-3">
          <p className="text-sm text-label-secondary">
            Bei genau 1 steht die Einzahl, sonst die Mehrzahl. Bekannte Wörter wie Kindern, Nächte und Erwachsenen
            passen sich auch ohne Zahlwort an, wenn sie direkt hinter der Zahl stehen.
          </p>
          {wordPresets.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {wordPresets.map((preset) => (
                <ToolbarButton
                  key={`${preset.one}/${preset.many}`}
                  disabled={disabled}
                  onClick={() => {
                    setWordOne(preset.one);
                    setWordMany(preset.many);
                  }}
                >
                  {`${preset.one}/${preset.many}`}
                </ToolbarButton>
              ))}
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <select className="eh-input min-w-40" value={wordKey} onChange={(event) => setWordKey(event.target.value)}>
              {wordFields.map((field) => (
                <option key={field.key} value={field.key}>
                  {field.label}
                </option>
              ))}
            </select>
            <input
              className="eh-input w-36"
              value={wordOne}
              placeholder="Einzahl, z. B. Kind"
              onChange={(event) => setWordOne(event.target.value)}
            />
            <input
              className="eh-input w-36"
              value={wordMany}
              placeholder="Mehrzahl, z. B. Kindern"
              onChange={(event) => setWordMany(event.target.value)}
            />
            <ToolbarButton disabled={disabled || !cleanRuleText(wordOne) || !cleanRuleText(wordMany)} onClick={insertWordChip}>
              Zahlwort einfügen
            </ToolbarButton>
          </div>
        </div>
      ) : null}
      <div
        ref={fieldRef}
        role="textbox"
        aria-multiline="true"
        aria-label="E-Mail-Text"
        contentEditable={!disabled}
        suppressContentEditableWarning
        className={`eh-input eh-mail-compose ${minHeightClass} w-full text-base leading-relaxed`}
        onFocus={onFocus}
        onKeyDown={(event) => {
          if (disabled || event.altKey || !(event.ctrlKey || event.metaKey)) return;
          const key = event.key.toLowerCase();
          if (key !== "b" && key !== "i" && key !== "u") return;
          event.preventDefault();
          applyInlineFormat(key === "b" ? "bold" : key === "i" ? "italic" : "underline");
        }}
        onMouseDown={(event) => {
          const target = event.target as HTMLElement;
          if (target.closest("[data-mail-if-remove], [data-mail-if-edit]")) event.preventDefault();
        }}
        onClick={(event) => {
          const target = event.target as HTMLElement;
          const remove = target.closest("[data-mail-if-remove]");
          const edit = target.closest("[data-mail-if-edit]");
          const block = (remove || edit)?.closest("[data-mail-block], [data-mail-if], [data-mail-unless]");
          if (remove && block) {
            event.preventDefault();
            releaseCondition(block);
            return;
          }
          if (edit && block instanceof HTMLElement) {
            event.preventDefault();
            beginEdit(block);
            return;
          }
          const token = target.closest("[data-mail-token], [data-mail-word]");
          if (!token) return;
          const selection = window.getSelection();
          const range = document.createRange();
          range.selectNode(token);
          selection?.removeAllRanges();
          selection?.addRange(range);
        }}
        onInput={emit}
      />
    </div>
  );
});

function ToolbarButton({
  children,
  disabled,
  onClick,
  title,
}: {
  children: string;
  disabled?: boolean;
  onClick: () => void;
  title?: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      title={title}
      className="rounded-lg border border-separator bg-card px-2.5 py-1 text-xs font-semibold text-label hover:bg-fill-tertiary disabled:opacity-50"
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
